import * as signalR from "@microsoft/signalr";

export interface ServerProgressUpdate {
  serverId: string;
  serverName: string;
  progress: number;
  status: string;
  message?: string;
  toolCount: number;
  resourceCount: number;
  promptCount: number;
  updatedAt: string;
  // Detailed processing counts
  processedModels: number;
  totalModels: number;
  processedTools: number;
  totalTools: number;
  // Non-zero only in the final Completed event
  suggestedMaskingRulesCount: number;
}

type ProgressCallback = (update: ServerProgressUpdate) => void;

class ServerGenerationHubService {
  private connection: signalR.HubConnection | null = null;
  private callbacks: Map<string, Set<ProgressCallback>> = new Map();
  private globalCallbacks: Set<ProgressCallback> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private subscribedServers: Set<string> = new Set();
  private isInitialized = false;
  private generationStartedCallbacks: Set<(serverId: string) => void> =
    new Set();

  constructor() {
    // Use base URL without /api suffix for SignalR hubs
    const apiUrl = process.env.REACT_APP_BASE_API_URL;

    if (!apiUrl) {
      throw new Error("REACT_APP_BASE_API_URL must be set at build time.");
    }
    const baseUrl = apiUrl.replace(/\/api$/, ""); // Remove /api suffix if present

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/serverGenerationHub`, {
        accessTokenFactory: () => {
          const token = localStorage.getItem("accessToken");
          return token || "";
        },
        timeout: 600000, // 10 minutes timeout for connection handshake
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount >= this.maxReconnectAttempts) {
            return null;
          }
          return this.reconnectDelay;
        },
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Configure server timeout - this is how long the client waits for messages from server
    this.connection.serverTimeoutInMilliseconds = 600000; // 10 minutes
    // Configure keep alive - ping interval to keep connection alive
    this.connection.keepAliveIntervalInMilliseconds = 300000; // 5 minutes

    this.connection.on("ProgressUpdate", (update: ServerProgressUpdate) => {
      this.notifyCallbacks(update.serverId, update);
      this.globalCallbacks.forEach((cb) => cb(update));
    });

    this.connection.onreconnected(async () => {
      this.reconnectAttempts = 0;
      // Resubscribe to all servers
      await this.resubscribeAll();
    });

    this.connection.onclose(async () => {
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        setTimeout(() => this.start(), this.reconnectDelay);
      }
    });
  }

  async start(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Disconnected) {
      try {
        await this.connection.start();
        this.isInitialized = true;
        this.reconnectAttempts = 0;

        // Resubscribe to all tracked servers after connection
        await this.resubscribeAll();
      } catch (err) {
        console.error("Error connecting to SignalR:", err);
        throw err;
      }
    } else if (
      this.connection?.state === signalR.HubConnectionState.Connected
    ) {
      this.isInitialized = true;
    }
  }

  async stop(): Promise<void> {
    if (this.connection) {
      this.isInitialized = false;
      await this.connection.stop();
    }
  }

  private async resubscribeAll(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      return;
    }

    const subscriptionPromises = Array.from(this.subscribedServers).map(
      async (serverId) => {
        try {
          await this.connection!.invoke("SubscribeToServer", serverId);
        } catch (err: any) {
          // Check if it's an authorization error
          if (
            err?.message?.includes("access denied") ||
            err?.message?.includes("not found")
          ) {
            console.warn(
              `Server ${serverId} not accessible - removing from tracked servers`,
            );
            this.subscribedServers.delete(serverId);
          } else {
            console.error(`Error resubscribing to server ${serverId}:`, err);
          }
        }
      },
    );

    await Promise.all(subscriptionPromises);
  }

  async subscribeToServer(
    serverId: string,
    callback?: ProgressCallback,
  ): Promise<void> {
    if (!this.connection) {
      throw new Error("SignalR connection not initialized");
    }

    // Ensure connection is started
    if (this.connection.state === signalR.HubConnectionState.Disconnected) {
      await this.start();
    }

    // Add callback if provided
    if (callback) {
      if (!this.callbacks.has(serverId)) {
        this.callbacks.set(serverId, new Set());
      }
      this.callbacks.get(serverId)!.add(callback);
    }

    // Track this server for resubscription
    this.subscribedServers.add(serverId);

    // Subscribe to server updates
    if (this.connection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.connection.invoke("SubscribeToServer", serverId);
      } catch (err: any) {
        console.error(`Error subscribing to server ${serverId}:`, err);

        // If it's an authorization error, don't retry
        if (
          err?.message?.includes("access denied") ||
          err?.message?.includes("not found")
        ) {
          console.warn(
            `Server ${serverId} not accessible - removing from tracked servers`,
          );
          this.subscribedServers.delete(serverId);
          return;
        }

        // For other errors, retry after a delay
        setTimeout(() => {
          if (this.subscribedServers.has(serverId)) {
            this.subscribeToServer(serverId).catch(console.error);
          }
        }, 2000);
      }
    }
  }

  async unsubscribeFromServer(
    serverId: string,
    callback?: ProgressCallback,
  ): Promise<void> {
    if (!this.connection) {
      return;
    }

    // Remove callback if provided
    if (callback) {
      const serverCallbacks = this.callbacks.get(serverId);
      if (serverCallbacks) {
        serverCallbacks.delete(callback);
        if (serverCallbacks.size === 0) {
          this.callbacks.delete(serverId);
        }
      }
    } else {
      // Remove all callbacks for this server
      this.callbacks.delete(serverId);
    }

    // Only unsubscribe if no more callbacks exist for this server
    if (!this.callbacks.has(serverId)) {
      // Remove from tracked servers
      this.subscribedServers.delete(serverId);

      // Unsubscribe from server updates if connected
      if (this.connection.state === signalR.HubConnectionState.Connected) {
        try {
          await this.connection.invoke("UnsubscribeFromServer", serverId);
        } catch (err) {
          console.error(`Error unsubscribing from server ${serverId}:`, err);
        }
      }
    }
  }

  private notifyCallbacks(
    serverId: string,
    update: ServerProgressUpdate,
  ): void {
    const serverCallbacks = this.callbacks.get(serverId);
    if (serverCallbacks) {
      serverCallbacks.forEach((callback) => callback(update));
    }
  }

  onGlobalProgress(callback: ProgressCallback): void {
    this.globalCallbacks.add(callback);
  }

  offGlobalProgress(callback: ProgressCallback): void {
    this.globalCallbacks.delete(callback);
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  onGenerationStarted(callback: (serverId: string) => void): void {
    this.generationStartedCallbacks.add(callback);
  }

  offGenerationStarted(callback: (serverId: string) => void): void {
    this.generationStartedCallbacks.delete(callback);
  }

  notifyGenerationStarted(serverId: string): void {
    this.generationStartedCallbacks.forEach((cb) => cb(serverId));
  }
}

// Singleton instance
const serverGenerationHubService = new ServerGenerationHubService();

export default serverGenerationHubService;
